import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { ButtonComponent } from '../button/button.component';

export interface SelectRiskDialogData {
  title?: string;
  subtitle?: string;
  selectedRiskLevel?: RiskLevelEnum | null;
}

@Component({
  selector: 'app-select-risk-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, DropdownComponent, ButtonComponent],
  templateUrl: './select-risk-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./select-risk-dialog.component.scss'],
})
export class SelectRiskDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<SelectRiskDialogComponent>);
  private readonly fb = inject(FormBuilder);
  readonly data = inject<SelectRiskDialogData>(MAT_DIALOG_DATA);

  readonly title = signal(this.data.title ?? 'Select Risk Level');

  private static readonly assignableRiskLevels: RiskLevelEnum[] = [
    RiskLevelEnum.Low,
    RiskLevelEnum.Medium,
    RiskLevelEnum.High,
    RiskLevelEnum.Severe,
  ];

  readonly riskLevelOptions = enumToFilterFields(RiskLevelEnum)
    .filter((option) => SelectRiskDialogComponent.assignableRiskLevels.includes(option.value))
    .map((option) => ({
      value: option.value,
      label: option.name.replace(/([a-z])([A-Z])/g, '$1 $2'),
    }));

  riskForm = this.fb.group({
    riskLevel: [
      SelectRiskDialogComponent.assignableRiskLevels.includes(this.data.selectedRiskLevel as RiskLevelEnum)
        ? (this.data.selectedRiskLevel as RiskLevelEnum)
        : null,
      Validators.required,
    ],
  });

  get continueButtonState(): 'disabled' | 'default' {
    return this.riskForm.invalid ? 'disabled' : 'default';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onContinue(): void {
    if (this.riskForm.invalid) {
      this.riskForm.markAllAsTouched();
      return;
    }

    this.dialogRef.close(this.riskForm.value.riskLevel);
  }
}
